import {EditorView, WidgetType} from '@codemirror/view'
import type {Element} from 'hast'
import {toDom} from 'hast-util-to-dom'

import {nodesEqual} from '../../language-tools/html/hast-utils'

/**
 * Widget that renders a hast node.
 *
 * Typically used to display elements that have no editable child content and must be rendered as complete replacement
 * decorations rather than wrapping decorations.
 */
export class HastElementWidget extends WidgetType {
  #node: Element
  #className: string | undefined

  /**
   * @param node - hast node to render.
   * @param className - Optional CSS class name to apply in addition to node.properties.class
   */
  constructor(node: Element, className?: string) {
    super()
    this.#node = node
    this.#className = className
  }

  override toDOM(_view: EditorView) {
    const result = toDom(this.#node)
    if (!(result instanceof HTMLElement)) throw new Error('HastElementWidget encountered non-element render result')
    if (this.#className) {
      result.classList.add(this.#className)
    }
    return result
  }

  override eq(widget: WidgetType): boolean {
    return (
      widget instanceof HastElementWidget &&
      nodesEqual(this.#node, widget.#node, {ignorePosition: true}) &&
      widget.#className === this.#className
    )
  }

  /**
   * Allow events to bubble so that CodeMirror can handle them natively.
   */
  override ignoreEvent() {
    return false
  }
}
